import { memo, useMemo, useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import type { Element, ElementCategory, Phase, Block } from "@/data/elements";

export type ColorMode = "category" | "phase" | "block" | "electronegativity" | "radius" | "discovery";

interface ElementTileProps {
  element: Element;
  onClick: (element: Element) => void;
  dimmed?: boolean;
  colorMode: ColorMode;
  focused?: boolean;
}

const categoryColorMap: Record<ElementCategory, string> = {
  "alkali-metal": "bg-cat-alkali",
  "alkaline-earth": "bg-cat-alkaline",
  "transition-metal": "bg-cat-transition",
  "post-transition-metal": "bg-cat-post-transition",
  "metalloid": "bg-cat-metalloid",
  "nonmetal": "bg-cat-nonmetal",
  "halogen": "bg-cat-halogen",
  "noble-gas": "bg-cat-noble",
  "lanthanide": "bg-cat-lanthanide",
  "actinide": "bg-cat-actinide",
  "unknown": "bg-cat-unknown",
};

const phaseColors: Record<Phase, string> = {
  solid: "hsl(25, 85%, 50%)",
  liquid: "hsl(200, 80%, 50%)",
  gas: "hsl(120, 50%, 45%)",
  unknown: "hsl(220, 10%, 50%)",
};

const blockColors: Record<Block, string> = {
  s: "hsl(4, 80%, 55%)",
  p: "hsl(210, 80%, 55%)",
  d: "hsl(45, 85%, 50%)",
  f: "hsl(120, 45%, 45%)",
};

function enGradient(val: number | null): string {
  if (val === null) return "hsl(220, 10%, 50%)";
  const t = Math.max(0, Math.min(1, (val - 0.7) / (3.98 - 0.7)));
  const h = 240 - t * 240;
  return `hsl(${h}, 70%, 50%)`;
}

function radiusGradient(val: number | null): string {
  if (val === null) return "hsl(220, 10%, 50%)";
  const t = Math.max(0, Math.min(1, (val - 25) / (270 - 25)));
  const h = t * 240;
  return `hsl(${h}, 70%, 50%)`;
}

function discoveryGradient(year: number | null): string {
  if (year === null) return "hsl(220, 10%, 50%)";
  // Ancient (<1700) → modern (2020)
  const t = Math.max(0, Math.min(1, (year - 1600) / (2020 - 1600)));
  const h = 240 - t * 200; // blue(ancient) → orange/red(modern)
  return `hsl(${h}, 65%, 50%)`;
}

function getIndicatorColor(element: Element, mode: ColorMode): string | null {
  switch (mode) {
    case "category": return null;
    case "phase": return phaseColors[element.phase];
    case "block": return blockColors[element.block];
    case "electronegativity": return enGradient(element.electronegativity);
    case "radius": return radiusGradient(element.atomicRadius);
    case "discovery": return discoveryGradient(element.discoveryYear);
  }
}

const ElementTile = memo(({ element, onClick, dimmed, colorMode, focused }: ElementTileProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tipPos, setTipPos] = useState<{ top: number; left: number; placeAbove: boolean } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const btnRef = useRef<HTMLButtonElement>(null);
  const indicatorColor = useMemo(() => getIndicatorColor(element, colorMode), [element, colorMode]);
  const useClass = colorMode === "category";

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => setShowTooltip(true), 300);
  };
  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setShowTooltip(false);
    setTipPos(null);
  };

  const phaseLabel = element.phase === "unknown" ? "-" : element.phase.charAt(0).toUpperCase() + element.phase.slice(1);

  useLayoutEffect(() => {
    if (!showTooltip || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const tipW = 180;
    const tipH = 140;
    const margin = 8;
    const placeAbove = r.top > tipH + margin;
    const top = placeAbove ? r.top - tipH - margin : r.bottom + margin;
    let left = r.left + r.width / 2 - tipW / 2;
    left = Math.max(8, Math.min(window.innerWidth - tipW - 8, left));
    setTipPos({ top, left, placeAbove });
  }, [showTooltip]);

  return (
    <button
      ref={btnRef}
      onClick={() => onClick(element)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        group relative flex flex-col items-center justify-center
        rounded-md border border-tile-border bg-tile-bg
        p-1 transition-all duration-100 cursor-pointer
        hover:scale-110 hover:z-10 hover:shadow-lg hover:bg-tile-hover
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
        ${dimmed ? "opacity-20" : "opacity-100"}
        ${focused ? "ring-2 ring-primary scale-105 z-10" : ""}
      `}
      style={{ gridRow: element.row, gridColumn: element.col }}
      aria-label={`${element.name}, atomic number ${element.number}`}
      data-element-number={element.number}
    >
      {useClass ? (
        <div className={`absolute top-0 left-0 right-0 h-0.5 rounded-t-sm ${categoryColorMap[element.category]}`} />
      ) : (
        <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-sm" style={{ backgroundColor: indicatorColor ?? undefined }} />
      )}
      <span className="text-[8px] leading-none text-muted-foreground">{element.number}</span>
      <span className="text-sm font-semibold leading-tight text-foreground">{element.symbol}</span>
      <span className="text-[7px] leading-none text-muted-foreground truncate w-full text-center">{element.name}</span>
      <span className="font-mono text-[7px] leading-none text-muted-foreground">
        {element.mass.toFixed(element.mass < 10 ? 3 : element.mass < 100 ? 2 : 1)}
      </span>

      {/* Hover tooltip — portal so overflow-x-auto on the grid wrapper doesn't clip it */}
      {showTooltip && tipPos && createPortal(
        <div
          className="fixed z-50 pointer-events-none"
          style={{ top: tipPos.top, left: tipPos.left, width: 180 }}
        >
          <div className="bg-popover border border-border rounded-lg shadow-lg p-2.5 text-left">
            <p className="text-xs font-semibold text-foreground mb-1">{element.name} ({element.symbol})</p>
            <div className="space-y-0.5 text-[11px]">
              <p className="text-muted-foreground">Mass: <span className="font-mono text-foreground">{element.mass.toFixed(3)} u</span></p>
              <p className="text-muted-foreground">Phase: <span className="text-foreground">{phaseLabel}</span></p>
              <p className="text-muted-foreground">Block: <span className="text-foreground">{element.block.toUpperCase()}</span></p>
              <p className="text-muted-foreground">Config: <span className="font-mono text-foreground text-[10px]">{element.electronConfig}</span></p>
              {element.electronegativity && (
                <p className="text-muted-foreground">EN: <span className="font-mono text-foreground">{element.electronegativity.toFixed(2)}</span></p>
              )}
              {element.density && (
                <p className="text-muted-foreground">Density: <span className="font-mono text-foreground">{element.density} g/cm³</span></p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </button>
  );
});

ElementTile.displayName = "ElementTile";

export default ElementTile;
